export interface EspecificacaoParts {
  norma?: string | null;
  nome?: string | null;
  formato?: string | null;
  acabamento?: string | null;
  diameter?: number | null;
  thickness?: number | null;
  width?: number | null;
  length?: number | null;
  quantityPieces?: number | null;
  diameterToleranceMinus?: number | null;
  diameterTolerancePlus?: number | null;
  thicknessToleranceMinus?: number | null;
  thicknessTolerancePlus?: number | null;
  widthToleranceMinus?: number | null;
  widthTolerancePlus?: number | null;
  lengthToleranceMinus?: number | null;
  lengthTolerancePlus?: number | null;
}

const numberFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

const SQUARE_FORMATS = ['quadrado', 'chapa'];
const TUBE_FORMATS = ['tubo', 'anel', 'tubular'];

const positive = (value?: number | null): number | null => {
  const num = typeof value === 'number' ? value : NaN;
  return Number.isFinite(num) && num > 0 ? num : null;
};

const text = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed !== '' ? trimmed : null;
};

const fmt = (value: number): string => numberFormatter.format(value);

// Tolerância informada inclui 0 ("-0" é um valor); só ausente/NaN conta como não informada.
const informed = (value?: number | null): number | null => {
  const num = typeof value === 'number' ? value : NaN;
  return Number.isFinite(num) ? Math.abs(num) : null;
};

// Sufixo (-m/+p) logo após o número da dimensão; lado ausente vale 0; os dois ausentes, sem sufixo.
const tolerance = (minus?: number | null, plus?: number | null): string => {
  const m = informed(minus);
  const p = informed(plus);
  if (m === null && p === null) return '';
  return `(-${fmt(m ?? 0)}/+${fmt(p ?? 0)})`;
};

const buildDimensoes = (parts: EspecificacaoParts): string => {
  const formatKey = (text(parts.formato) || '').toLowerCase();
  const thickness = positive(parts.thickness);
  const width = positive(parts.width);
  const diameter = positive(parts.diameter);
  const length = positive(parts.length);
  const lengthTol = tolerance(parts.lengthToleranceMinus, parts.lengthTolerancePlus);

  if (SQUARE_FORMATS.includes(formatKey)) {
    const segments = [
      thickness !== null ? `${fmt(thickness)}${tolerance(parts.thicknessToleranceMinus, parts.thicknessTolerancePlus)}` : null,
      width !== null ? `${fmt(width)}${tolerance(parts.widthToleranceMinus, parts.widthTolerancePlus)}` : null,
      length !== null ? `${fmt(length)}${lengthTol}` : null,
    ].filter((v): v is string => v !== null);
    return segments.length > 0 ? `# ${segments.join(' x ')}MM` : '';
  }

  if (TUBE_FORMATS.includes(formatKey)) {
    const heads = [
      thickness !== null ? `ØEXT. ${fmt(thickness)}${tolerance(parts.thicknessToleranceMinus, parts.thicknessTolerancePlus)}` : null,
      diameter !== null ? `ØINT. ${fmt(diameter)}${tolerance(parts.diameterToleranceMinus, parts.diameterTolerancePlus)}` : null,
    ].filter((v): v is string => v !== null);
    if (heads.length === 0 && length === null) return '';
    const head = heads.join(' X ');
    if (length === null) return `${head}MM`;
    const tail = `${fmt(length)}${lengthTol}`;
    return head ? `${head} x ${tail}MM` : `${tail}MM`;
  }

  const segments = [
    diameter !== null ? `Ø${fmt(diameter)}${tolerance(parts.diameterToleranceMinus, parts.diameterTolerancePlus)}` : null,
    length !== null ? `${fmt(length)}${lengthTol}` : null,
  ].filter((v): v is string => v !== null);
  return segments.length > 0 ? `${segments.join(' x ')}MM` : '';
};

/**
 * Monta a especificação comercial do item:
 * Norma + Nome + FORMATO + ACABAMENTO + Dimensões + Quantidade.
 * Componentes vazios são omitidos; saída em caixa alta.
 * Ex.: "INCONEL 625 REDONDO PRÉ USINADO Ø114,3 x 58MM - 62 PÇ(S)"
 * Com tolerância: "... REDONDO CASCUDO Ø23(-0/+3) x 555MM - 23 PÇ(S)"
 */
export const buildEspecificacao = (parts: EspecificacaoParts): string => {
  const dimensoes = buildDimensoes(parts);
  const pieces = positive(parts.quantityPieces);

  const segments = [
    text(parts.norma),
    text(parts.nome),
    text(parts.formato),
    text(parts.acabamento),
    dimensoes || null,
  ].filter((v): v is string => v !== null);

  if (segments.length === 0) return '';

  const base = segments.join(' ');
  const withPieces = pieces !== null ? `${base} - ${fmt(pieces)} PÇ(S)` : base;
  return withPieces.toUpperCase();
};
